/**
 * UserDetailModal Component
 * Shows comprehensive user details with edit, delete, and resend invitation capabilities
 */

import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "../../services/supabase";
import {
  updateProfile,
  inviteUserViaEdgeFunction,
} from "../../services/auth.service";
import {
  searchOrganizationOptions,
  organizationLabel,
} from "../../services/pickerOptions.service";
import SearchableSelect from "../common/SearchableSelect";
import { getLocations } from "../../services/locations.service";
import { USER_ROLES, QUERY_KEYS } from "../../utils/constants";
import { UserRole } from "../../types/auth.types";
import type { Profile } from "../../types/auth.types";
import type { UUID } from "../../types/common.types";
import Modal from "../common/Modal";
import Button from "../common/Button";
import Select from "../common/Select";
import Input from "../common/Input";
import Badge from "../ui/Badge";
import StatusToggle from "../common/StatusToggle";
import { useConfirmDialog } from "../ui/ConfirmDialog";
import { useToast } from "../ui/Toast";
import { formatDate } from "../../utils/formatters";

interface UserDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: Profile | null;
  onUserUpdated: () => void;
  onUserDeleted: () => void;
}

interface UserStatus {
  status: "Active" | "Inactive" | "Pending";
  emailConfirmed: boolean;
  lastSignIn: string | null;
}

interface OrganizationAccess {
  organization_id: string;
  organization_name: string;
  location_ids: string[] | null;
  location_names: string[];
}

const UserDetailModal: React.FC<UserDetailModalProps> = ({
  isOpen,
  onClose,
  user,
  onUserUpdated,
  onUserDeleted,
}) => {
  const queryClient = useQueryClient();
  const { confirm, ConfirmDialogComponent } = useConfirmDialog();
  const { showToast } = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const [editedUser, setEditedUser] = useState<Partial<Profile>>({});
  const [errorMessage, setErrorMessage] = useState("");

  // Organization/Location editing state (for partner users)
  const [selectedOrgId, setSelectedOrgId] = useState<string>("");
  const [selectedLocationIds, setSelectedLocationIds] = useState<UUID[]>([]);
  const [allLocationsSelected, setAllLocationsSelected] = useState(false);

  // Determine user status from profile data
  // For now, assume all users with profiles are active
  // This is a temporary fix until we resolve orphaned profiles
  const userStatus: UserStatus | undefined = user
    ? (() => {
        // If user has logged in at least once, they're confirmed
        const hasLoggedIn = user.last_login !== null;
        const status: "Active" | "Inactive" | "Pending" = hasLoggedIn
          ? user.is_active
            ? "Active"
            : "Inactive"
          : "Pending";

        return {
          status,
          emailConfirmed: hasLoggedIn,
          lastSignIn: user.last_login,
        };
      })()
    : undefined;

  // Fetch user organization access
  const { data: organizationAccess = [] } = useQuery<OrganizationAccess[]>({
    queryKey: [QUERY_KEYS.USERS, "access", user?.id],
    queryFn: async () => {
      if (!user?.id) {
        return [];
      }

      const { data: access, error } = await supabase
        .from("user_organization_access")
        .select(
          "organization_id, location_ids, organization:organizations(name)",
        )
        .eq("user_id", user.id);

      if (error) {
        throw error;
      }

      const result: OrganizationAccess[] = [];

      for (const item of access || []) {
        const orgName =
          (item.organization as any)?.name || "Unknown Organization";
        let locationNames: string[] = [];

        if (item.location_ids && item.location_ids.length > 0) {
          const { data: locations } = await supabase
            .from("locations")
            .select("name")
            .in("id", item.location_ids);

          locationNames = locations?.map((loc) => loc.name) || [];
        }

        result.push({
          organization_id: item.organization_id,
          organization_name: orgName,
          location_ids: item.location_ids,
          location_names: locationNames,
        });
      }

      return result;
    },
    enabled: !!user?.id && isOpen,
  });

  // Partner organization picker: server-side search (SearchableSelect below). It used to list
  // only the first 100 organizations by name, out of ~2,800 in PROD.

  // Fetch locations for selected organization
  const { data: locationsData } = useQuery({
    // Distinct key per data shape (2026-10-16): the same key held different rows/columns on other
    // screens, and with the 5-minute staleTime whichever screen fetched first won.
    queryKey: [QUERY_KEYS.LOCATIONS, selectedOrgId, "by-name"],
    queryFn: () => {
      if (!selectedOrgId) {
        return Promise.resolve({ data: [], total: 0, page: 1, limit: 100 });
      }
      return getLocations(selectedOrgId, {
        page: 1,
        limit: 100,
        sortBy: "name",
        sortOrder: "asc",
      });
    },
    enabled: !!selectedOrgId && isOpen && isEditing,
  });

  // Filter locations to only show those with facility_id (for Partner role)
  const locationsWithFacilityId =
    locationsData?.data?.filter((loc: any) => loc.facility_id) || [];
  const shouldShowLocationSelector =
    isEditing &&
    user?.role === UserRole.PARTNER &&
    selectedOrgId &&
    locationsWithFacilityId.length > 0;

  useEffect(() => {
    if (user) {
      setEditedUser({
        role: user.role,
        full_name: user.full_name,
        phone_number: user.phone_number,
      });
      // Clear any error messages when switching users
      setErrorMessage("");
      setIsEditing(false);
    }
  }, [user]);

  // Initialize org/location state when organizationAccess changes
  useEffect(() => {
    if (organizationAccess.length > 0 && user?.role === UserRole.PARTNER) {
      const access = organizationAccess[0];
      setSelectedOrgId(access.organization_id);
      // If location_ids is null or empty array, it means "All Locations"
      if (access.location_ids === null || access.location_ids.length === 0) {
        setAllLocationsSelected(true);
        setSelectedLocationIds([]);
      } else {
        setAllLocationsSelected(false);
        setSelectedLocationIds(access.location_ids as UUID[]);
      }
    }
  }, [organizationAccess, user?.role]);

  // Update user mutation
  const updateUserMutation = useMutation({
    mutationFn: async (updates: Partial<Profile>) => {
      if (!user?.id) {
        throw new Error("No user selected");
      }
      return await updateProfile(user.id, updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.USERS] });
      setIsEditing(false);
      setErrorMessage("");
      onUserUpdated();
    },
    onError: (error) => {
      setErrorMessage(
        error instanceof Error ? error.message : "Failed to update user",
      );
    },
  });

  // Delete user mutation (only for inactive users)
  // This performs a soft delete - marks user as deleted and obscures their information
  const deleteUserMutation = useMutation({
    mutationFn: async (userId: UUID) => {
      if (!user) {
        throw new Error("No user selected");
      }

      // Only allow deletion of inactive users
      if (user.is_active) {
        throw new Error(
          "Cannot delete an active user. Please deactivate the user first using the toggle.",
        );
      }

      console.log("[UserDetailModal] Soft deleting inactive user:", userId);

      // Get current user ID for audit trail
      const {
        data: { user: currentUser },
      } = await supabase.auth.getUser();
      if (!currentUser) {
        throw new Error("Not authenticated");
      }

      // Archive the user before deleting
      const archiveData = {
        user_id: userId,
        email: user.email,
        full_name: user.full_name,
        phone_number: user.phone_number,
        role: user.role,
        organization_id: organizationAccess[0]?.organization_id || null,
        location_ids: organizationAccess[0]?.location_ids || null,
        created_at: user.created_at,
        last_login: user.last_login,
        deleted_by: currentUser.id,
        deleted_at: new Date().toISOString(),
        original_profile: user,
      };

      const { error: archiveError } = await supabase
        .from("deleted_users_archive")
        .insert(archiveData);

      if (archiveError) {
        console.error("[UserDetailModal] Archive error:", archiveError);
        // Continue with deletion even if archive fails (log the error but don't block)
        console.warn(
          "[UserDetailModal] Continuing with deletion despite archive error",
        );
      }

      // Soft delete: mark as deleted and obscure information
      const deletedEmail = `deleted_${userId}@deleted.local`;
      const { error: updateError } = await supabase
        .from("profiles")
        .update({
          email: deletedEmail,
          full_name: "Deleted User",
          is_active: false,
          phone_number: "",
        })
        .eq("id", userId);

      if (updateError) {
        console.error("[UserDetailModal] Soft delete error:", updateError);
        throw new Error(`Failed to delete user: ${updateError.message}`);
      }

      console.log("[UserDetailModal] User soft deleted successfully");
    },
    onSuccess: async () => {
      console.log("[UserDetailModal] Delete onSuccess called");
      showToast("success", "User deleted successfully");

      // Force refetch the users list
      await queryClient.refetchQueries({ queryKey: [QUERY_KEYS.USERS] });
      console.log("[UserDetailModal] Users list refetched");

      onUserDeleted();
      onClose();
    },
    onError: (error) => {
      console.error("[UserDetailModal] Delete onError called:", error);
      const message =
        error instanceof Error ? error.message : "Failed to delete user";
      setErrorMessage(message);
      showToast("error", message);
    },
  });

  // Resend invitation mutation
  const resendInvitationMutation = useMutation({
    mutationFn: async () => {
      console.log("[UserDetailModal] Resend invitation starting");
      if (!user?.email || !user?.role) {
        throw new Error("Missing user information");
      }

      const orgAccess = organizationAccess[0];
      const orgId = orgAccess?.organization_id;
      const locationIds = orgAccess?.location_ids || [];

      console.log("[UserDetailModal] Resending invitation to:", user.email);
      await inviteUserViaEdgeFunction(
        user.email,
        user.role,
        orgId,
        locationIds,
        user.full_name,
        true, // resend = true
      );
    },
    onSuccess: () => {
      console.log("[UserDetailModal] Resend invitation successful");
      setErrorMessage("");
      showToast("success", "Invitation resent successfully!");
      onClose();
    },
    onError: (error) => {
      console.error("[UserDetailModal] Resend invitation error:", error);
      const message =
        error instanceof Error ? error.message : "Failed to resend invitation";
      setErrorMessage(message);
      showToast("error", message);
    },
  });

  // Toggle active status mutation
  const toggleActiveMutation = useMutation({
    mutationFn: async (isActive: boolean) => {
      console.log(
        "[UserDetailModal] Toggle active mutation starting, isActive:",
        isActive,
      );
      if (!user?.id) {
        throw new Error("No user selected");
      }

      const result = await updateProfile(user.id, {
        is_active: isActive,
      } as Partial<Profile>);
      console.log("[UserDetailModal] Toggle active successful");
      return result;
    },
    onSuccess: async (_updatedUser, isActive) => {
      console.log("[UserDetailModal] Toggle active onSuccess called");
      const action = isActive ? "activated" : "deactivated";
      showToast("success", `User ${action} successfully`);

      // Force refetch the users list - this will update the UI
      await queryClient.refetchQueries({ queryKey: [QUERY_KEYS.USERS] });
      console.log("[UserDetailModal] Users list refetched after toggle");

      onUserUpdated();
    },
    onError: (error) => {
      console.error("[UserDetailModal] Toggle active error:", error);
      const message =
        error instanceof Error ? error.message : "Failed to update status";
      setErrorMessage(message);
      showToast("error", message);
    },
  });

  // Update organization access mutation (for partner users)
  const updateOrgAccessMutation = useMutation({
    mutationFn: async () => {
      if (!user?.id) {
        throw new Error("No user selected");
      }
      if (!selectedOrgId) {
        throw new Error("Organization is required for partner users");
      }

      console.log("[UserDetailModal] Updating org access for partner:", {
        userId: user.id,
        orgId: selectedOrgId,
        allLocations: allLocationsSelected,
        locationIds: selectedLocationIds,
      });

      // For partner role: if "All Locations" selected, pass null for org-level access
      // Otherwise pass selected location IDs
      const locationIds = allLocationsSelected
        ? null
        : selectedLocationIds.length > 0
          ? selectedLocationIds
          : null;

      // Upsert the user_organization_access record
      const { error } = await supabase.from("user_organization_access").upsert(
        {
          user_id: user.id,
          organization_id: selectedOrgId,
          location_ids: locationIds,
        },
        {
          onConflict: "user_id,organization_id",
        },
      );

      if (error) {
        console.error("[UserDetailModal] Failed to update org access:", error);
        throw new Error(
          `Failed to update organization access: ${error.message}`,
        );
      }

      console.log("[UserDetailModal] Org access updated successfully");
    },
    onSuccess: () => {
      // Refetch the organization access data
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.USERS, "access", user?.id],
      });
    },
    onError: (error) => {
      console.error("[UserDetailModal] Update org access error:", error);
      const message =
        error instanceof Error
          ? error.message
          : "Failed to update organization access";
      setErrorMessage(message);
      showToast("error", message);
    },
  });

  const handleSave = async () => {
    // Update profile
    updateUserMutation.mutate(editedUser);

    // If partner user and org access changed, update it
    if (user?.role === UserRole.PARTNER && selectedOrgId) {
      await updateOrgAccessMutation.mutateAsync();
    }
  };

  const handleDelete = async () => {
    console.log("[UserDetailModal] handleDelete called");
    if (!user) {
      console.log("[UserDetailModal] No user selected, returning");
      return;
    }

    const isDeactivate = user.is_active;
    const action = isDeactivate ? "deactivate" : "delete";
    const title = isDeactivate ? "Deactivate User" : "Delete User";
    const message = isDeactivate
      ? `Are you sure you want to deactivate ${user.full_name || user.email}? They will not be able to log in until reactivated.`
      : `Are you sure you want to permanently delete ${user.full_name || user.email}? This action cannot be undone.`;

    console.log(`[UserDetailModal] Showing ${action} confirmation dialog`);
    const confirmed = await confirm({
      title,
      message,
      confirmText: isDeactivate ? "Deactivate" : "Delete",
      variant: "danger",
    });

    console.log("[UserDetailModal] Confirmation result:", confirmed);
    if (confirmed) {
      console.log(
        `[UserDetailModal] User confirmed ${action}, calling mutation`,
      );
      deleteUserMutation.mutate(user.id);
    } else {
      console.log(`[UserDetailModal] User cancelled ${action}`);
    }
  };

  const handleToggleActive = (newStatus: boolean) => {
    console.log(
      "[UserDetailModal] handleToggleActive called with newStatus:",
      newStatus,
    );
    if (!user) {
      console.log("[UserDetailModal] No user selected, returning");
      return;
    }

    console.log(
      "[UserDetailModal] Calling toggle mutation with newStatus:",
      newStatus,
    );
    toggleActiveMutation.mutate(newStatus);
  };

  const handleResendInvitation = () => {
    resendInvitationMutation.mutate();
  };

  const getStatusBadgeVariant = (status?: string) => {
    switch (status) {
      case "Active":
        return "success";
      case "Inactive":
        return "default";
      case "Pending":
        return "warning";
      default:
        return "default";
    }
  };

  if (!user) {
    return null;
  }

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={user.full_name || user.email}
        size="lg"
      >
        <div className="space-y-6">
          {/* Error Message */}
          {errorMessage && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-800">{errorMessage}</p>
            </div>
          )}

          {/* Status Badge */}
          <div className="flex items-center gap-2">
            <Badge variant={getStatusBadgeVariant(userStatus?.status)}>
              {userStatus?.status || "Loading..."}
            </Badge>
            {!userStatus?.emailConfirmed && (
              <Badge variant="warning">Email Not Verified</Badge>
            )}
          </div>

          {/* User Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <p className="text-sm text-gray-900">{user.email}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Role
              </label>
              {isEditing ? (
                <Select
                  value={editedUser.role || user.role}
                  onChange={(e) =>
                    setEditedUser({
                      ...editedUser,
                      role: e.target.value as UserRole,
                    })
                  }
                  fullWidth
                >
                  {USER_ROLES.map((role) => (
                    <option key={role.value} value={role.value}>
                      {role.label}
                    </option>
                  ))}
                </Select>
              ) : (
                <p className="text-sm text-gray-900">
                  {USER_ROLES.find((r) => r.value === user.role)?.label ||
                    user.role}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Full Name
              </label>
              {isEditing ? (
                <Input
                  value={editedUser.full_name || ""}
                  onChange={(e) =>
                    setEditedUser({ ...editedUser, full_name: e.target.value })
                  }
                  fullWidth
                />
              ) : (
                <p className="text-sm text-gray-900">
                  {user.full_name || "N/A"}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone Number
              </label>
              {isEditing ? (
                <Input
                  value={editedUser.phone_number || ""}
                  onChange={(e) =>
                    setEditedUser({
                      ...editedUser,
                      phone_number: e.target.value,
                    })
                  }
                  fullWidth
                />
              ) : (
                <p className="text-sm text-gray-900">
                  {user.phone_number || "N/A"}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Created
              </label>
              <p className="text-sm text-gray-900">
                {formatDate(user.created_at)}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Last Login
              </label>
              <p className="text-sm text-gray-900">
                {userStatus?.lastSignIn
                  ? formatDate(userStatus.lastSignIn)
                  : user.last_login
                    ? formatDate(user.last_login)
                    : "Never"}
              </p>
            </div>
          </div>

          {/* Organization Access */}
          {isEditing && user.role === UserRole.PARTNER ? (
            // Editable organization/location selector for partner users
            <div className="space-y-4">
              <SearchableSelect
                label="Organization"
                value={selectedOrgId}
                onChange={(v) => {
                  setSelectedOrgId(v);
                  setSelectedLocationIds([]);
                  setAllLocationsSelected(false);
                }}
                search={searchOrganizationOptions}
                resolveLabel={organizationLabel}
                queryKeyBase="user-detail-partner-organization"
                emptyLabel="Select Organization"
                placeholder="Search organizations..."
                testId="user-partner-organization"
              />

              {/* Partner Location Selection */}
              {shouldShowLocationSelector && (
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Location Access
                  </label>

                  {/* "All Locations" Option */}
                  <div className="border border-gray-300 rounded-lg p-4">
                    <label className="flex items-center space-x-2 mb-3">
                      <input
                        type="checkbox"
                        checked={allLocationsSelected}
                        onChange={(e) => {
                          setAllLocationsSelected(e.target.checked);
                          if (e.target.checked) {
                            setSelectedLocationIds([]);
                          }
                        }}
                        className="h-4 w-4 text-blue-600 rounded"
                      />
                      <span className="text-sm font-semibold text-gray-900">
                        All Locations
                      </span>
                    </label>

                    {/* Individual Locations */}
                    {!allLocationsSelected && (
                      <div className="pl-6 space-y-2 border-t border-gray-200 pt-3">
                        <p className="text-xs text-gray-600 mb-2">
                          Or select specific locations:
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                          {locationsWithFacilityId.map((location: any) => (
                            <label
                              key={location.id}
                              className="flex items-center space-x-2"
                            >
                              <input
                                type="checkbox"
                                checked={selectedLocationIds.includes(
                                  location.id,
                                )}
                                onChange={() => {
                                  setSelectedLocationIds((prev) =>
                                    prev.includes(location.id)
                                      ? prev.filter((id) => id !== location.id)
                                      : [...prev, location.id],
                                  );
                                }}
                                className="h-4 w-4 text-blue-600 rounded"
                              />
                              <span className="text-sm text-gray-700">
                                {location.name}
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <p className="text-xs text-gray-500">
                    {allLocationsSelected
                      ? "This partner will have access to all locations within the organization."
                      : selectedLocationIds.length > 0
                        ? `This partner will have access to ${selectedLocationIds.length} selected location(s).`
                        : 'Please select "All Locations" or choose specific locations.'}
                  </p>
                </div>
              )}
            </div>
          ) : organizationAccess.length > 0 ? (
            // Read-only display of organization access
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Organization Access
              </label>
              <div className="space-y-2">
                {organizationAccess.map((access, index) => (
                  <div key={index} className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm font-medium text-gray-900">
                      {access.organization_name}
                    </p>
                    <p className="text-xs text-gray-600 mt-1">
                      {access.location_ids === null ||
                      access.location_ids.length === 0
                        ? "All locations"
                        : `${access.location_names.length} location(s): ${access.location_names.join(", ")}`}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {/* Active Status Toggle - Only show for non-pending users */}
          {!isEditing && userStatus?.status !== "Pending" && (
            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-1">
                    Account Status
                  </label>
                  <p className="text-xs text-gray-600">
                    {user.is_active
                      ? "User can log in and access the system"
                      : "User is deactivated and cannot log in"}
                  </p>
                </div>
                <StatusToggle
                  checked={user.is_active}
                  onChange={handleToggleActive}
                  disabled={toggleActiveMutation.isPending}
                  size="md"
                />
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-wrap gap-2 pt-4 border-t">
            {!isEditing ? (
              <>
                <Button variant="primary" onClick={() => setIsEditing(true)}>
                  Edit
                </Button>

                {userStatus?.status === "Pending" && (
                  <Button
                    variant="secondary"
                    onClick={handleResendInvitation}
                    isLoading={resendInvitationMutation.isPending}
                  >
                    Resend Invitation
                  </Button>
                )}

                {!user.is_active && userStatus?.status !== "Pending" && (
                  <Button
                    variant="danger"
                    onClick={handleDelete}
                    isLoading={deleteUserMutation.isPending}
                  >
                    Delete User
                  </Button>
                )}

                <Button
                  variant="secondary"
                  onClick={onClose}
                  className="ml-auto"
                >
                  Close
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="primary"
                  onClick={handleSave}
                  isLoading={
                    updateUserMutation.isPending ||
                    updateOrgAccessMutation.isPending
                  }
                >
                  Save Changes
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setIsEditing(false);
                    setEditedUser({
                      role: user.role,
                      full_name: user.full_name,
                      phone_number: user.phone_number,
                    });
                    // Reset org/location state to original values
                    if (
                      organizationAccess.length > 0 &&
                      user.role === UserRole.PARTNER
                    ) {
                      const access = organizationAccess[0];
                      setSelectedOrgId(access.organization_id);
                      if (
                        access.location_ids === null ||
                        access.location_ids.length === 0
                      ) {
                        setAllLocationsSelected(true);
                        setSelectedLocationIds([]);
                      } else {
                        setAllLocationsSelected(false);
                        setSelectedLocationIds(access.location_ids as UUID[]);
                      }
                    }
                  }}
                >
                  Cancel
                </Button>
              </>
            )}
          </div>
        </div>
      </Modal>

      <ConfirmDialogComponent />
    </>
  );
};

export default UserDetailModal;
